import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Shield, Zap, Sparkles } from 'lucide-react';

export default function PaymentMethodPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tier = searchParams.get('tier') || 'pro';
  const billing = searchParams.get('billing') || 'monthly';

  const plans = {
    starter: { name: 'Identity Starter', monthly: 4.99, annual: 39.99, icon: Shield },
    pro: { name: 'Align Pro', monthly: 9.99, annual: 79.99, icon: Zap },
    elite: { name: 'Elite Pioneer', monthly: 24.99, annual: 199.99, icon: Sparkles },
  };

  const selectedPlan = plans[tier] || plans.pro;
  const price = billing === 'monthly' ? selectedPlan.monthly : selectedPlan.annual;
  const PlanIcon = selectedPlan.icon;

  const selectStripe = () => {
    navigate(`/checkout/stripe?tier=${tier}&billing=${billing}`);
  };

  const selectRazorpay = () => {
    navigate(`/checkout/razorpay?tier=${tier}&billing=${billing}`);
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] dark:bg-[#121212] flex items-center justify-center p-4 transition-colors duration-200">
      <div className="w-full max-w-md bg-card border border-border rounded-3xl shadow-xl overflow-hidden dark:bg-zinc-950 bg-white p-6 space-y-6">
        
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/checkout')}
            className="p-2 hover:bg-neutral-100 dark:hover:bg-zinc-900 rounded-full transition"
            aria-label="Back to plans"
          >
            <ArrowLeft className="h-5 w-5 text-foreground" />
          </button>
          <div>
            <h2 className="text-xl font-bold text-foreground">Select Payment Method</h2>
            <p className="text-xs text-muted-foreground">Choose your payment gateway</p>
          </div>
        </div>

        {/* Selected Plan Summary */}
        <div className="bg-neutral-50 dark:bg-zinc-900/50 p-4 rounded-2xl border border-border/60 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <PlanIcon className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold text-sm text-foreground">{selectedPlan.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{billing} billing</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-bold text-foreground">${price}</p>
            <p className="text-[10px] text-muted-foreground">due today</p>
          </div>
        </div>

        {/* Gateway Selection */}
        <div className="space-y-4">
          {/* Stripe Option */}
          <button
            onClick={selectStripe}
            className="w-full p-4 rounded-2xl border border-border dark:bg-zinc-900 bg-white hover:border-emerald-500 hover:ring-2 hover:ring-emerald-500/20 text-left transition flex items-center justify-between group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold text-lg">
                S
              </div>
              <div>
                <p className="font-bold text-foreground group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition">Stripe Checkout</p>
                <p className="text-xs text-muted-foreground">Pay securely via Credit or Debit Card</p>
              </div>
            </div>
            <CreditCard className="h-5 w-5 text-muted-foreground group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition" />
          </button>

          {/* Razorpay Option */}
          <button
            onClick={selectRazorpay}
            className="w-full p-4 rounded-2xl border border-border dark:bg-zinc-900 bg-white hover:border-emerald-500 hover:ring-2 hover:ring-emerald-500/20 text-left transition flex items-center justify-between group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-lg">
                R
              </div>
              <div>
                <p className="font-bold text-foreground group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition">Razorpay Checkout</p>
                <p className="text-xs text-muted-foreground">Pay with UPI, Card, Netbanking, or Wallet</p>
              </div>
            </div>
            <div className="w-5 h-5 rounded-full border-2 border-muted-foreground flex items-center justify-center text-[10px] font-bold text-muted-foreground group-hover:border-emerald-600 dark:group-hover:border-emerald-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition">
              ₹
            </div>
          </button>
        </div>

        <div className="text-center">
          <p className="text-[10px] text-muted-foreground">
            🔒 Payments are fully simulated. No real currency is exchanged.
          </p>
        </div>

      </div>
    </div>
  );
}
